import { useState } from 'react';
import { urlFor } from '../lib/imageBuilder';
import { AnnotatedScreensModal } from './AnnotatedScreensModal';

// Default view for an Annotated Screens content block.
// Shows 2–5 mobile mockups in a row with subtle hot-dot affordances overlaid.
// Clicking any screen opens the interactive lightbox with annotations.
export const AnnotatedScreens = ({ screens }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  if (!screens || screens.length === 0) return null;

  const openModal = (idx) => {
    setActiveIndex(idx);
    setModalOpen(true);
  };

  // Cap visual width per screen so layouts stay clean for 2–5 items.
  const count = Math.min(screens.length, 5);

  return (
    <>
      <div
        className="my-12 flex flex-row flex-wrap gap-6 md:gap-8 justify-center items-start"
        data-testid="annotated-screens"
      >
        {screens.map((screen, idx) => (
          <button
            key={idx}
            onClick={() => openModal(idx)}
            className="group flex flex-col items-center cursor-zoom-in text-left"
            style={{
              width: `calc(${100 / count}% - ${count > 1 ? '1.5rem' : '0px'})`,
              minWidth: '160px',
              maxWidth: '260px',
            }}
            aria-label={`Open ${screen.caption || `screen ${idx + 1}`} in detail view`}
            data-testid={`annotated-screen-${idx}`}
          >
            {screen.caption && (
              <p className="mb-3 font-body text-sm text-foreground-secondary text-center w-full">
                {screen.caption}
              </p>
            )}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-lg shadow-foreground-primary/10 group-hover:shadow-xl transition-shadow">
              {/* Inner layer scales as a whole so image AND hot-dots stay aligned on hover. */}
              <div className="relative transition-transform duration-500 ease-out group-hover:scale-105">
                {screen.image?.asset && (
                  <img
                    src={urlFor(screen.image).width(600).url()}
                    alt={screen.image?.alt || screen.caption || `Screen ${idx + 1}`}
                    className="w-full h-auto block"
                  />
                )}

                {/* Subtle hot-dot affordances at hotspot positions */}
                {(screen.hotspots || []).map((hs, hIdx) =>
                  typeof hs.x === 'number' && typeof hs.y === 'number' ? (
                  <span
                    key={hIdx}
                    className="absolute flex items-center justify-center w-6 h-6 pointer-events-none"
                    style={{
                      left: `${hs.x}%`,
                      top: `${hs.y}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                    aria-hidden="true"
                  >
                      <span className="absolute w-5 h-5 rounded-full bg-accent/65 animate-pulse-soft" />
                      <span className="relative block w-3 h-3 rounded-full bg-accent border-2 border-white shadow-md" />
                    </span>
                  ) : null,
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Modal only mounts while open so its internal state (currentScreenIndex, visited)
          fully resets each time the user reopens it. */}
      {modalOpen && (
        <AnnotatedScreensModal
          screens={screens}
          initialIndex={activeIndex}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

export default AnnotatedScreens;
